import { ArrowUpRight, Info } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import type { Integration } from '@/api/types';
import { Button, ErrorNote, TextField } from '@/components/ui';
import { RequestIntegrationDialog } from '@/features/integrations/RequestDialogs';
import { useConnectFlow } from './useConnectFlow';

/**
 * The connect form for one integration: requirements, connect_fields (Shopify store,
 * stock symbols), credential_fields (PayPal, secrets masked), then the right button.
 */
export function ConnectPanel({
  integration,
  next = '/',
  compact,
  initialFields,
  submitLabel,
}: {
  integration: Integration;
  next?: string;
  compact?: boolean;
  initialFields?: Record<string, string>;
  submitLabel?: string;
}) {
  const { connect, busy, outcome } = useConnectFlow(next);
  const [values, setValues] = useState<Record<string, string>>(() => ({ ...(initialFields ?? {}) }));
  const [requestOpen, setRequestOpen] = useState(false);
  const [local, setLocal] = useState<Record<string, string>>({});
  const comingSoon = integration.status === 'partner_required' || outcome?.kind === 'coming_soon';
  const serverFields = outcome?.kind === 'invalid' ? outcome.fields : {};
  const errors = { ...serverFields, ...local };

  const connectFields = integration.connect_fields ?? [];
  const credentialFields = integration.auth === 'api_key' ? (integration.credential_fields ?? []) : [];

  async function submit(e: FormEvent) {
    e.preventDefault();
    const missing: Record<string, string> = {};
    for (const f of [...connectFields, ...credentialFields]) {
      if (!(values[f.key] ?? '').trim()) missing[f.key] = `Enter your ${f.label.toLowerCase()}.`;
    }
    setLocal(missing);
    if (Object.keys(missing).length) return;
    const pick = (fs: typeof connectFields) => Object.fromEntries(fs.map((f) => [f.key, (values[f.key] ?? '').trim()]));
    await connect(integration, {
      ...(connectFields.length ? { fields: pick(connectFields) } : {}),
      ...(credentialFields.length ? { credentials: pick(credentialFields) } : {}),
    });
  }

  if (comingSoon) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted-ink">
          {outcome?.kind === 'coming_soon' && integration.status !== 'partner_required'
            ? outcome.message
            : `${integration.name} needs MyDash to be an approved partner first. Ask for it and we’ll tell you when it’s ready.`}
        </p>
        <div>
          <Button variant="primary" onClick={() => setRequestOpen(true)}>
            Request {integration.name}
          </Button>
        </div>
        <RequestIntegrationDialog open={requestOpen} onClose={() => setRequestOpen(false)} integration={integration} />
      </div>
    );
  }

  const label =
    submitLabel ??
    (integration.auth === 'oauth2' ? `Continue to ${integration.name}` : `Connect ${integration.name}`);

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      {!compact && (
        <p className="flex gap-2 text-sm text-muted-ink">
          <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>{integration.user_requirements}</span>
        </p>
      )}
      {outcome?.kind === 'invalid' && <ErrorNote title="Check the details" message={outcome.message} />}
      {connectFields.map((f) => (
        <TextField
          key={f.key}
          label={f.label}
          placeholder={f.placeholder ?? undefined}
          value={values[f.key] ?? ''}
          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
          error={errors[f.key]}
          type={f.secret ? 'password' : 'text'}
          autoComplete="off"
          spellCheck={false}
          hint={f.key === 'symbols' ? 'Separate symbols with commas. Add .TO for Toronto listings.' : undefined}
        />
      ))}
      {credentialFields.map((f) => (
        <TextField
          key={f.key}
          label={f.label}
          placeholder={f.placeholder ?? undefined}
          value={values[f.key] ?? ''}
          onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
          error={errors[f.key]}
          type={f.secret ? 'password' : 'text'}
          autoComplete={f.secret ? 'new-password' : 'off'}
          spellCheck={false}
        />
      ))}
      {credentialFields.length > 0 && (
        <p className="text-xs text-muted-ink">Your keys are encrypted and only used to read your account totals.</p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="submit"
          variant="primary"
          loading={busy}
          icon={integration.auth === 'oauth2' ? <ArrowUpRight className="size-4" aria-hidden /> : undefined}
        >
          {label}
        </Button>
        {integration.demo_available && <span className="text-xs text-muted-ink">On this server it connects with demo data.</span>}
      </div>
    </form>
  );
}
